/**
 * THEATER/DANCE (F4): works (scripts/choreographies) with linked scene
 * ELEMENTS (props/wardrobe/roles), associated music (dance), and gallery.
 * Group CATEGORY determines default work type (theater -> script,
 * dance -> choreography); proper name assigned by church.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  elementsOfWork,
  getSceneElementsQuery,
  getScenePhotosQuery,
  getSceneWorksQuery,
  getSongsQuery,
  patchRowVerified,
  queryKeys,
  sceneReadiness,
  workKindForCategory,
} from "@agape/shared";
import {
  type ChurchRecord,
  eq,
  type PersonRecord,
  scene_element,
  scene_photo,
  scene_work,
  type SceneElementRecord,
  type ScenePhotoRecord,
  type SceneWorkRecord,
  type SongRecord,
} from "@agape/powersync";
import { Drama, Plus, Puzzle, Trash2 } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog, TextArea, UploadField } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function SceneSection(props: {
  groupId: string;
  categoryId: string | null | undefined;
  church: ChurchRecord | undefined;
  people: readonly PersonRecord[];
}) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const works = useDomainQuery<SceneWorkRecord>(
    (uid) => queryKeys.domain.sceneWorks(uid),
    (db) => getSceneWorksQuery(db),
  );
  const elements = useDomainQuery<SceneElementRecord>(
    (uid) => queryKeys.domain.sceneElements(uid),
    (db) => getSceneElementsQuery(db),
  );
  const photos = useDomainQuery<ScenePhotoRecord>(
    (uid) => queryKeys.domain.scenePhotos(uid),
    (db) => getScenePhotosQuery(db),
  );
  const songs = useDomainQuery<SongRecord>(
    (uid) => queryKeys.domain.songs(uid),
    (db) => getSongsQuery(db),
  );

  const scope = () => [props.groupId, props.church?.id];
  const canWrite = () => grants.can(scope(), "scene-elements", "write");
  const isDance = () => props.categoryId === "dance";
  const defaultKind = () => workKindForCategory(props.categoryId);

  const myWorks = createMemo(() => (works.data ?? []).filter((w) => w.group_id === props.groupId));
  const myElements = createMemo(() =>
    (elements.data ?? []).filter((e) => e.group_id === props.groupId),
  );
  const mySongs = createMemo(() => (songs.data ?? []).filter((s) => s.group_id === props.groupId));
  const photosOf = (workId: string) => (photos.data ?? []).filter((p) => p.work_id === workId);

  // ── Crear obra ─────────────────────────────────────────────────────────────
  const [workOpen, setWorkOpen] = createSignal(false);
  const [workTitle, setWorkTitle] = createSignal("");
  const [workContent, setWorkContent] = createSignal("");
  const [workSong, setWorkSong] = createSignal("");
  const [workMusicUrl, setWorkMusicUrl] = createSignal("");
  const addWork = async () => {
    if (!workTitle().trim()) {
      return;
    }
    await write()
      .insert(scene_work)
      .values({
        id: newId(),
        church_id: props.church?.id,
        group_id: props.groupId,
        kind: defaultKind(),
        title: workTitle().trim(),
        content: workContent().trim() || null,
        song_id: workSong() || null,
        music_url: workMusicUrl().trim() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setWorkOpen(false);
    setWorkTitle("");
    setWorkContent("");
    setWorkSong("");
    setWorkMusicUrl("");
    showToast(isDance() ? t(msg`Choreography created`) : t(msg`Script created`), "success");
  };
  const trashWork = async (id: string) => {
    try {
      await patchRowVerified(write(), scene_work, scene_work.id, id, { deleted_at: nowIso() });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };

  // ── Add element ────────────────────────────────────────────────────────────
  const [elemWork, setElemWork] = createSignal<string | null>(null);
  const [elemOpen, setElemOpen] = createSignal(false);
  const [elemKind, setElemKind] = createSignal<"prop" | "costume" | "role">("prop");
  const [elemName, setElemName] = createSignal("");
  const [elemPerson, setElemPerson] = createSignal("");
  const [elemQty, setElemQty] = createSignal("1");
  const addElement = async () => {
    if (!elemName().trim()) {
      return;
    }
    await write()
      .insert(scene_element)
      .values({
        id: newId(),
        church_id: props.church?.id,
        group_id: props.groupId,
        work_id: elemWork(),
        kind: elemKind(),
        name: elemName().trim(),
        quantity: Number(elemQty()) || 1,
        assigned_person_id: elemPerson() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setElemOpen(false);
    setElemName("");
    setElemPerson("");
    setElemQty("1");
    showToast(t(msg`Scene element added`), "success");
  };
  const removeElement = async (id: string) => {
    await write().delete(scene_element).where(eq(scene_element.id, id));
  };

  const addPhoto = async (workId: string, url: string) => {
    const count = photosOf(workId).length;
    await write()
      .insert(scene_photo)
      .values({
        id: newId(),
        church_id: props.church?.id,
        group_id: props.groupId,
        work_id: workId,
        url,
        sort_order: count + 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    showToast(t(msg`Photo added`), "success");
  };

  const songTitleOf = (id: string | null) => mySongs().find((s) => s.id === id)?.title ?? null;
  const kindLabel = (kind: string | null) =>
    kind === "costume" ? t(msg`Costume`) : kind === "role" ? t(msg`Role`) : t(msg`Prop`);

  return (
    <section class="space-y-2" data-testid="scene-section">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {isDance() ? t(msg`Choreographies`) : t(msg`Scripts`)}
        </h3>
        <Show when={canWrite()}>
          <Button
            size="sm"
            variant="ghost"
            data-testid="scene-work-create"
            onClick={() => setWorkOpen(true)}
          >
            <Drama size={14} /> {isDance() ? t(msg`New choreography`) : t(msg`New script`)}
          </Button>
        </Show>
      </div>

      <Show
        when={myWorks().length > 0}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="scene-empty">
            {t(msg`No works yet.`)}
          </p>
        }
      >
        <For each={myWorks()}>
          {(work) => {
            const workElements = createMemo(() => elementsOfWork(myElements(), work.id));
            const readiness = createMemo(() => sceneReadiness(workElements()));
            return (
              <Card data-testid="scene-work-item" data-work-id={work.id}>
                <CardContent class="space-y-2 py-3">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-medium">{work.title}</p>
                      <Show when={isDance() && (work.song_id || work.music_url)}>
                        <p class="truncate text-xs text-muted-foreground">
                          {t(msg`Music`)}: {songTitleOf(work.song_id) ?? work.music_url}
                        </p>
                      </Show>
                      <p class="text-xs text-muted-foreground" data-testid="scene-readiness">
                        {(() => {
                          const assignedRoles = readiness().byKind.role.assigned;
                          const totalRoles = readiness().byKind.role.total;
                          return t(msg`${assignedRoles}/${totalRoles} roles cast`);
                        })()}
                        <Show when={readiness().ready}>
                          {" "}
                          · <span class="font-semibold text-primary">{t(msg`ready`)}</span>
                        </Show>
                      </p>
                    </div>
                    <Show when={canWrite()}>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={t(msg`Move to trash`)}
                        onClick={() => void trashWork(work.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </Show>
                  </div>

                  <Show when={work.content}>
                    <p class="whitespace-pre-wrap text-xs text-muted-foreground">{work.content}</p>
                  </Show>

                  {/* Elementos vinculados */}
                  <div class="space-y-1 border-t border-border/60 pt-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(msg`Scene elements`)}
                      </span>
                      <Show when={canWrite()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid="scene-element-add"
                          onClick={() => {
                            setElemWork(work.id);
                            setElemKind("prop");
                            setElemOpen(true);
                          }}
                        >
                          <Puzzle size={13} /> {t(msg`Add element`)}
                        </Button>
                      </Show>
                    </div>
                    <For each={workElements()}>
                      {(el) => (
                        <div
                          class="flex items-center justify-between gap-2 text-sm"
                          data-testid="scene-element-item"
                          data-element-kind={el.kind}
                        >
                          <span class="min-w-0 truncate">
                            <span class="text-muted-foreground">{kindLabel(el.kind)}:</span>{" "}
                            {el.name}
                            <Show when={el.quantity && el.quantity > 1}> ×{el.quantity}</Show>
                            <Show when={el.assigned_name}>
                              <span class="text-muted-foreground"> · {el.assigned_name}</span>
                            </Show>
                          </span>
                          <Show when={canWrite()}>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t(msg`Remove element`)}
                              onClick={() => void removeElement(el.id)}
                            >
                              <Trash2 size={12} />
                            </Button>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>

                  {/* Galería */}
                  <div class="space-y-1 border-t border-border/60 pt-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(msg`Gallery`)}
                      </span>
                      <Show when={canWrite()}>
                        <div class="w-48">
                          <UploadField
                            id={`scene-photo-${work.id}`}
                            label=""
                            testId="scene-photo-upload"
                            onUploaded={(url) => void addPhoto(work.id, url)}
                          />
                        </div>
                      </Show>
                    </div>
                    <Show when={photosOf(work.id).length > 0}>
                      <div class="flex flex-wrap gap-2">
                        <For each={photosOf(work.id)}>
                          {(photo) => (
                            <img
                              src={photo.url ?? ""}
                              alt={photo.caption ?? work.title ?? ""}
                              loading="lazy"
                              decoding="async"
                              class="h-16 w-16 rounded-md object-cover"
                              data-testid="scene-photo"
                            />
                          )}
                        </For>
                      </div>
                    </Show>
                  </div>
                </CardContent>
              </Card>
            );
          }}
        </For>
      </Show>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={workOpen()}
        onOpenChange={setWorkOpen}
        title={isDance() ? t(msg`New choreography`) : t(msg`New script`)}
        icon={<Drama size={20} />}
        submitTestId="scene-work-submit"
        disabled={!workTitle().trim()}
        onSubmit={addWork}
      >
        <Field
          id="sw-title"
          label={t(msg`Title`)}
          value={workTitle()}
          onInput={setWorkTitle}
          testId="scene-work-title-input"
        />
        <Field id="sw-content" label={isDance() ? t(msg`Notes`) : t(msg`Script`)}>
          <TextArea
            id="sw-content"
            value={workContent()}
            onInput={setWorkContent}
            rows={4}
            testId="scene-work-content-input"
          />
        </Field>
        <Show when={isDance()}>
          <Field id="sw-song" label={t(msg`Music from the library`)}>
            <Select
              options={[
                { value: "", label: t(msg`None`) },
                ...mySongs().map((s) => ({ value: s.id, label: s.title ?? s.id })),
              ]}
              value={workSong()}
              onChange={setWorkSong}
              aria-label={t(msg`Music`)}
            />
          </Field>
          <Field
            id="sw-music-url"
            label={t(msg`…or a music link`)}
            value={workMusicUrl()}
            onInput={setWorkMusicUrl}
            placeholder="https://…"
          />
        </Show>
      </FormDialog>

      <FormDialog
        open={elemOpen()}
        onOpenChange={setElemOpen}
        title={t(msg`Add scene element`)}
        icon={<Plus size={20} />}
        submitTestId="scene-element-submit"
        disabled={!elemName().trim()}
        onSubmit={addElement}
      >
        <div class="grid grid-cols-2 gap-3">
          <Field id="se-kind" label={t(msg`Type`)}>
            <Select<"prop" | "costume" | "role">
              options={[
                { value: "prop", label: t(msg`Prop`) },
                { value: "costume", label: t(msg`Costume`) },
                { value: "role", label: t(msg`Role`) },
              ]}
              value={elemKind()}
              onChange={setElemKind}
              aria-label={t(msg`Type`)}
            />
          </Field>
          <Field
            id="se-qty"
            label={t(msg`Quantity`)}
            type="number"
            value={elemQty()}
            onInput={setElemQty}
          />
        </div>
        <Field
          id="se-name"
          label={t(msg`Name`)}
          value={elemName()}
          onInput={setElemName}
          testId="scene-element-name-input"
        />
        <Field id="se-person" label={t(msg`Assigned to`)}>
          <Select
            options={[
              { value: "", label: t(msg`Unassigned`) },
              ...props.people.map((p) => ({ value: p.id, label: p.name ?? p.id })),
            ]}
            value={elemPerson()}
            onChange={setElemPerson}
            aria-label={t(msg`Assigned to`)}
          />
        </Field>
      </FormDialog>
    </section>
  );
}
